"use client";

import { memo, useCallback, useState } from "react";
import { ChevronDownIcon, DownloadIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useGeneratedDocumentStore } from "@/components/document/generatedDocumentStore";
import { useAuth } from "@/lib/auth/AuthContext";
import { apiAuthFetch, openPraetorCaseDocument } from "@/lib/authFetch";
import { ClientLogger } from "@/lib/clientLogger";
import { usePdfErrorStore } from "@/lib/stores/pdfErrorStore";
import { SaveOrReviseButton } from "@/components/document/SaveOrReviseButton";

/**
 * Prominent action bar shown in the PDF sidebar header for generated documents.
 * Replaces the prior icon-only stack (PDF + DOCX download icons + save-icon) with
 * three labeled buttons:
 *
 *   [ Stáhnout ▾ ]  [ Otevřít v Praetoru ]  [ Uložit do spisu ▾ ]
 *
 * "Otevřít v Praetoru" only surfaces when this generated doc has a live Praetor
 * link (`praetorDocumentId` + `praetorLinkAlive`) AND the session is Bearer-scheme
 * (the postMessage handshake to the Praetor host only works when the assistant is
 * embedded inside Praetor — Basic-auth dev runs have no host to post to).
 *
 * "Stáhnout" merges the two prior icon buttons into a single labeled button with a
 * dropdown for the format. The 503 X-Conversion-Failed retry surface is preserved
 * locally — same shape as `GeneratedDocControls.DownloadButton`, just labelled.
 */
export const DocumentActionBar = memo(function DocumentActionBar({
  docId,
  title,
  versionId,
}: {
  docId: string;
  title: string;
  versionId?: string;
}) {
  const { t } = useTranslation();
  const { scheme } = useAuth();
  const entry = useGeneratedDocumentStore((s) => s.entries[docId]);
  const praetorDocumentId = entry?.praetorDocumentId;
  const praetorLinkAlive = entry?.praetorLinkAlive === true;
  const showOpenInPraetor = scheme === "Bearer" && !!praetorDocumentId && praetorLinkAlive;

  const [downloading, setDownloading] = useState<"pdf" | "docx" | null>(null);

  const handleDownload = useCallback(
    async (format: "pdf" | "docx") => {
      if (downloading) return;
      setDownloading(format);
      try {
        const url = versionId
          ? `/api/documents/${docId}/download?format=${format}&version=${versionId}`
          : `/api/documents/${docId}/download?format=${format}`;
        const res = await apiAuthFetch(url);

        // Transient DOCX→PDF conversion failure: surface the same retry-aware dialog
        // the inline card uses, with the retry callback firing this same download.
        if (
          res.status === 503 &&
          res.headers.get("X-Conversion-Failed") === "1"
        ) {
          usePdfErrorStore.getState().show(
            "documents.conversionServiceUnavailable",
            { title },
            () => { void handleDownload(format); },
          );
          return;
        }

        if (res.status === 406) {
          const { toast } = await import("sonner");
          toast.error(t("documents.wordFormatUnavailable"));
          return;
        }

        if (!res.ok) {
          const { toast } = await import("sonner");
          toast.error(t("documents.downloadFailed"));
          return;
        }

        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = `${title}.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      } catch (err) {
        ClientLogger.error("DocumentActionBar download failed", { docId, format, err });
        const { toast } = await import("sonner");
        toast.error(t("documents.downloadFailed"));
      } finally {
        setDownloading(null);
      }
    },
    [docId, title, versionId, downloading, t],
  );

  const handleOpenInPraetor = useCallback(() => {
    if (!praetorDocumentId) return;
    try {
      openPraetorCaseDocument(praetorDocumentId);
    } catch (err) {
      ClientLogger.error("openPraetorCaseDocument from sidebar failed", {
        error: err instanceof Error ? err : undefined,
        component: "DocumentActionBar",
        metadata: { docId, praetorDocumentId },
      });
    }
  }, [docId, praetorDocumentId]);

  return (
    <div className="flex items-center gap-1.5">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={downloading !== null}
            aria-label={t("pdfToolbar.download")}
          >
            {downloading ? <Spinner size="sm" /> : <DownloadIcon className="size-3.5" />}
            <span>{t("pdfToolbar.download")}</span>
            <ChevronDownIcon className="size-3.5 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => handleDownload("pdf")}>
            {t("pdfToolbar.downloadPdf")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleDownload("docx")}>
            {t("pdfToolbar.downloadDocx")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {showOpenInPraetor && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleOpenInPraetor}
        >
          <img src="/open-icon.png" alt="" className="size-3.5 rounded object-cover" />
          <span>{t("pdf.openInPraetor")}</span>
        </Button>
      )}

      <SaveOrReviseButton docId={docId} appearance="label" />
    </div>
  );
});
